// The selection bar's Set value…: one field, one value, on every ticked line. The field's own cell
// editor (the calendar, the list, the several-values list, text) picks the value; every line is checked
// as a typed cell (`fill.ts`): lines that have it already, and read-only ones, are skipped and said so.
// Apply saves one line after another through the writer — one entry per line, one Ctrl+Z for all.

import { useMemo, useRef, useState } from "react";
import { cleanListValue, isMulti, joinValues, multiValueProblem } from "@app/shared";
import type { ListValueRecord, Mode, Row } from "@app/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import type { ColumnDef } from "./columns";
import { useDialogDrag } from "./dialog-drag";
import {
  DateEditor,
  ListEditor,
  MomentEditor,
  MultiListEditor,
  SelectEditor,
  SuggestEditor,
  TextEditor,
} from "./editors";
import type { EditorProps } from "./editors";
import type { FillPlan, MultiMode } from "./fill";
import { fillPreview, planFill } from "./fill";
import { useMessage } from "./message";
import { closesOnlyByHand } from "./shipping-dialogs";

/** The fields Set value offers: the columns the warehouse fills in ("Warehouse fills in"), Review never. */
export const settableColumns = (columns: readonly ColumnDef[]) =>
  columns.filter((c) => c.kind === "edit" && c.field && c.id !== "review");

export function SetValueDialog({
  rows,
  columns,
  mode,
  today,
  listValues,
  usage,
  manager,
  onAddValue,
  onApply,
  onClose,
}: {
  /** The ticked lines' rows. */
  rows: readonly Row[];
  columns: readonly ColumnDef[];
  mode: Mode;
  today: string;
  listValues: readonly ListValueRecord[];
  usage: (column: ColumnDef) => ReadonlyMap<string, number>;
  manager: boolean;
  onAddValue: (column: ColumnDef, typed: string, done: (value: string | null) => void) => void;
  onApply: (column: ColumnDef, plan: FillPlan) => void;
  onClose: () => void;
}) {
  const drag = useDialogDrag();
  const message = useMessage();
  const choices = settableColumns(columns);
  const select = useRef<HTMLSelectElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [columnId, setColumnId] = useState(
    () => (choices.find((c) => c.id === "routingStatus") ?? choices[0])?.id ?? "",
  );
  const column = choices.find((c) => c.id === columnId);
  /** The value picked or typed (null: none yet; "" clears the field) and whether its editor is open. */
  const [value, setValue] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [multi, setMulti] = useState<MultiMode>("replace");
  const plan = useMemo(
    () =>
      column && value !== null
        ? planFill(
            column,
            rows.map((row) => ({ row, raw: value })),
            { today, listValues, mode, multi },
          )
        : null,
    [column, value, rows, today, listValues, mode, multi],
  );

  const pick = (v: string) => {
    setValue(v);
    setEditing(false);
  };
  const editor = (c: ColumnDef) => {
    const props: EditorProps = {
      column: c,
      value: value ?? "",
      anchor: box,
      listValues: listValues.filter((v) => v.list === c.list),
      usage: usage(c),
      commit: (v) => pick(v),
      cancel: () => setEditing(false),
      addValue: (typed, _move, alongWith = []) => {
        const problem = multiValueProblem(c.field, typed);
        if (problem) return void message.error(`Can't add to ${c.header}`, problem);
        onAddValue(c, cleanListValue(typed), (added) => {
          if (added !== null) pick(isMulti(c.field) ? joinValues([...alongWith, added]) : added);
        });
      },
    };
    switch (c.editor) {
      case "date":
        return <DateEditor {...props} />;
      case "select":
        return <SelectEditor {...props} />;
      case "list":
        return isMulti(c.field) ? (
          <MultiListEditor {...props} manager={manager} />
        ) : (
          <ListEditor {...props} manager={manager} />
        );
      case "suggest":
        return <SuggestEditor {...props} />;
      case "moment":
        return <MomentEditor {...props} />;
      default:
        return <TextEditor {...props} />;
    }
  };

  const count = plan?.writes.length ?? 0;
  return (
    <Dialog open onOpenChange={closesOnlyByHand(onClose)}>
      <DialogContent ref={drag.ref} style={drag.style} initialFocus={select} className="sm:max-w-lg">
        <DialogHeader {...drag.handle}>
          <DialogTitle>
            Set a value on {rows.length} line{rows.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Each line is saved as if typed in its cell, with the same checks, and gets its own System log entry. Ctrl+Z
            puts them all back.
          </DialogDescription>
        </DialogHeader>
        <label className="grid gap-1 text-sm">
          <span className="font-medium">Field</span>
          <select
            ref={select}
            className="border-input bg-background h-9 rounded-md border px-2 text-sm"
            value={columnId}
            onChange={(e) => {
              setColumnId(e.target.value);
              setValue(null);
              setEditing(false);
              setMulti("replace");
            }}
            data-testid="set-value-field"
          >
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.header}
              </option>
            ))}
          </select>
        </label>
        {column ? (
          <div className="grid gap-1 text-sm">
            <span className="font-medium">Value</span>
            <div
              ref={box}
              className={cn(
                "relative flex h-9 items-center overflow-hidden rounded-md border",
                editing && "ring-primary/40 ring-2",
              )}
              data-testid="set-value-box"
            >
              {editing ? (
                editor(column)
              ) : (
                <button
                  type="button"
                  className={cn("h-full w-full px-2 text-left", value === null && "text-muted-foreground")}
                  onClick={() => setEditing(true)}
                >
                  {value === null ? `Choose ${column.header}…` : value === "" ? "(empty: clears the field)" : value}
                </button>
              )}
            </div>
            {editing ? (
              <span className="text-muted-foreground text-xs">
                Pick a value, or type it and press Enter · Esc goes back
              </span>
            ) : null}
          </div>
        ) : null}
        {column && isMulti(column.field) ? (
          <div className="flex gap-4 text-sm" role="radiogroup" aria-label="Several values">
            {(
              [
                ["replace", "Replace"],
                ["add", "Add to what each line has"],
              ] as const
            ).map(([m, label]) => (
              <label key={m} className="flex items-center gap-1.5">
                <input type="radio" className="accent-primary" checked={multi === m} onChange={() => setMulti(m)} />
                {label}
              </label>
            ))}
          </div>
        ) : null}
        {column && plan ? (
          <p
            className={cn("text-sm", plan.refused ? "text-destructive" : "text-muted-foreground")}
            data-testid="set-value-preview"
          >
            {plan.refused
              ? `${plan.refused.message} (${plan.refused.row.line.customerName})`
              : fillPreview(column, plan)}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!column || !plan || !!plan.refused || editing}
            onClick={() => {
              if (!column || !plan) return;
              onClose();
              onApply(column, plan);
            }}
            data-testid="set-value-apply"
          >
            Apply to {count} line{count === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
